/**
 * Шлюз отвечает на accept/decline не исключением, а union'ом: отказ приезжает обычным полем
 * ответа (`GlobalError`/`InputError`), и промис при этом резолвится. Поэтому «сервер выполнил»
 * отличимо от «сервер отказал» только по `__typename` — код, который этого не проверяет,
 * принимает отказ за успех и молчит.
 */
export type InviteMutationResult =
  | {
      __typename?: string;
      /** Исход `Operation`: `OK` - выполнено, `ERROR` и `NOT_FOUND` - сервер не выполнил. */
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      errors?: ({ messagesErrors?: (string | null)[] | null } | null)[] | null;
    }
  | null
  | undefined;

/** Причина отказа. Код нужен, чтобы подменить технические сообщения сервера своим текстом. */
export type InviteFailure = { code?: string; message?: string };

/** @returns `null`, если сервер выполнил действие, иначе — причину отказа. */
export const resolveInviteFailure = (data: InviteMutationResult): InviteFailure | null => {
  if (data?.__typename === 'Operation') {
    // `Operation` с `ERROR` или `NOT_FOUND` - тоже ответ без исключения, но действия не было:
    // объявлять вступление состоявшимся по одному типу ответа нельзя.
    return data.status && data.status !== 'OK' ? {} : null;
  }

  if (data?.__typename === 'GlobalError') {
    return { code: data.errorCode ?? undefined, message: data.errorMessage ?? undefined };
  }

  if (data?.__typename === 'InputError') {
    return { message: data.errors?.[0]?.messagesErrors?.[0] ?? undefined };
  }

  // Пустой или незнакомый ответ: действие не подтверждено, и объявлять его успешным нельзя.
  return {};
};

/**
 * Отказы, которые означают не сбой, а устаревший экран.
 *
 * `limit.error` — последнее место ушло, пока человек читал приглашение. `already.member` — он
 * уже в составе: висело приглашение, а кнопка «Войти в команду» осталась от прошлого рендера
 * (состав берётся из кеша, а приглашение приезжает по WS — вот и всё окно).
 *
 * `already.participating` — пересечение по времени: человек уже принят в другую сессию, которая
 * идёт в те же часы (`enterSession` проверяет это через `findAnySessionIdByUserAndPeriod`). Тоже
 * не сбой: обе сессии существуют, просто вместе они не складываются.
 *
 * Реакция на все три одинаковая и отличается от реакции на сбой: перечитать состав и сказать
 * спокойно, без красного тоста. Сайт работает — отстал экран, и через секунду он покажет правду.
 */
const STALE_STATE_CODES = new Set([
  'post.session.limit.error',
  'post.session.already.member',
  'post.session.already.participating',
]);

export const isStaleStateFailure = (failure: InviteFailure): boolean =>
  Boolean(failure.code && STALE_STATE_CODES.has(failure.code));

/**
 * Текст отказа для человека. Известные коды подменяем своими строками: у сервера они
 * локализуются через `config_app.localization`, а при отсутствии записи `messageSource` отдаёт
 * сам код («post.session.discord.required»), который в тосте читается как сбой сайта.
 */
export const resolveInviteFailureText = (
  failure: InviteFailure,
  texts: {
    discordRequired: string;
    teamFull: string;
    alreadyMember: string;
    alreadyParticipating: string;
    fallback: string;
  }
): string => {
  if (failure.code === 'post.session.discord.required') {
    return texts.discordRequired;
  }

  // Пересечение сессий. Текст всегда свой: в `message` у этого кода лежит не фраза для
  // человека, а id мешающей сессии (см. `resolveOverlapSessionId`).
  if (failure.code === 'post.session.already.participating') {
    return texts.alreadyParticipating;
  }

  if (failure.code === 'post.session.limit.error') {
    return texts.teamFull;
  }

  if (failure.code === 'post.session.already.member') {
    return texts.alreadyMember;
  }

  // Нет записи в `config_app.localization` - сервер подставляет в сообщение сам код. Это не
  // фраза для человека, а служебный идентификатор, и в тосте он читается как сбой сайта.
  if (failure.message && failure.message !== failure.code) {
    return failure.message;
  }

  return texts.fallback;
};

/**
 * Заявка, чья сессия помешала вступить, — по ней тост даёт ссылку «открыть».
 *
 * Сервер нашёл мешающую сессию при проверке пересечения и кладёт в `errorMessage` id её
 * заявки: у этого кода поле несёт не текст (его пишет клиент), а адрес. Именно заявки —
 * своей страницы у сессии нет, она живёт на странице заявки.
 *
 * Ссылку строим только на UUID: старый сервер на этот код присылал либо null, либо
 * локализованную фразу, и на ней кнопка вела бы в никуда. Не сошлось — тост без кнопки.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const resolveOverlapPostId = (failure: InviteFailure): string | null => {
  if (failure.code !== 'post.session.already.participating') {
    return null;
  }

  const value = failure.message?.trim();
  return value && UUID_RE.test(value) ? value : null;
};

/**
 * Текст отказа для перехода между сессиями.
 *
 * Отдельно от {@link resolveInviteFailureText}, потому что у перехода три своих исхода, которых
 * нет у приглашения, и один общий читается иначе: `already.participating` при вступлении значит
 * «у вас уже есть сессия в это время», а при переходе - «у вас есть ЕЩЁ ОДНА сессия в это время»,
 * потому что первую переход только что освободил.
 *
 * До 22.09.2026 всё, кроме двух случаев, читалось как «переход не прошёл, попробуйте ещё раз».
 * Повтор помогает ровно при обрыве связи: Discord от нажатия не привяжется, занятое место не
 * освободится, а при двух пересечениях подряд каждый переход упирается в следующую сессию.
 */
export const resolveSwitchFailureText = (
  failure: InviteFailure,
  texts: {
    hasMembers: string;
    nothingToLeave: string;
    targetEnded: string;
    discordRequired: string;
    teamFull: string;
    alreadyMember: string;
    secondClash: string;
    fallback: string;
  }
): string => {
  if (failure.code === 'post.session.switch.has.members') {
    return texts.hasMembers;
  }
  if (failure.code === 'post.session.switch.nothing.to.leave') {
    return texts.nothingToLeave;
  }
  if (failure.code === 'post.session.ended') {
    return texts.targetEnded;
  }

  return resolveInviteFailureText(failure, {
    discordRequired: texts.discordRequired,
    teamFull: texts.teamFull,
    alreadyMember: texts.alreadyMember,
    alreadyParticipating: texts.secondClash,
    fallback: texts.fallback,
  });
};
